import * as React from 'react';
import cn from 'classnames';
import { saveAs } from 'file-saver';
import { BaseModal, Button, Textarea } from 'src/components';
import useModal from 'src/hooks/useModal';
import { useAlerts } from 'src/common/AlertManager';
import { useScanMigrationUrlsMutation } from 'src/api/mutations/migrationMutations';
import { MigrationScanResult, MigrationScanVerdict } from 'src/types/TMigration';
import { ExampleFileLink, ModalHeader, Note, SegmentBar, Stat, errorText } from './parts';
import styles from './styles.module.scss';

/** Wide enough for a six-column stat row, and scrollable rather than taller
 *  than the viewport. */
const MODAL_SIZE = { width: 'min(56rem, 92vw)', maxHeight: '88vh', overflowY: 'auto' } as const;

/**
 * "Check your URLs" — the safety check, run before anything is changed.
 *
 * A dialog, like every other one-off action in the dashboard (adding a schema,
 * adding an analytics destination). An earlier version opened a panel below the
 * button, which pushed the rest of the page down and left the reader unsure
 * whether they were looking at a form or at another block of settings.
 *
 * It is the URL translator run over a list: it creates nothing, contacts nobody
 * and can be run as often as the customer likes. Worth saying on the dialog
 * itself, because "check my URLs" sounds like it might change something.
 *
 * It answers a different question from the library copy. The copy asks *what is
 * in your library*, which we can read from the provider. This asks *what do
 * your URLs ask for*, which only the customer's own traffic answers — the same
 * library can be served by six sizes or by two hundred transformations, and
 * only the second case has anything to worry about. A competitor can only
 * answer this with a documentation table.
 */

/** Plain wording for the reasons migration-onramp reports. The raw strings are a stable
 *  vocabulary shared with logs and support, so they are translated here rather
 *  than renamed at the source. */
const REFUSAL_COPY: Record<string, string> = {
  video: 'Video URLs — we do not resize video on the fly',
  'cloudinary-private': 'Cloudinary “private” images',
  'cloudinary-authenticated': 'Cloudinary “authenticated” images',
  'cloudinary-fetch': 'Cloudinary “fetch” URLs (images on someone else’s site)',
  'unknown-delivery-id': 'An account id we do not recognise',
  'alias-not-found': 'No matching image',
};

/** The same reasons, short enough to sit beside a URL. */
const REFUSAL_SHORT: Record<string, string> = {
  video: 'video',
  'cloudinary-private': 'private',
  'cloudinary-authenticated': 'authenticated',
  'cloudinary-fetch': 'fetch',
};

const VERDICT_TITLE: Record<MigrationScanVerdict, string> = {
  identical: 'These parameters can be translated',
  approximate: 'Some settings cannot be carried over',
  refused: 'These will not work',
  unparseable: 'We could not read these lines',
};

const VERDICT_CSV: Record<MigrationScanVerdict, string> = {
  identical: 'translated',
  approximate: 'settings changed',
  refused: 'will not work',
  unparseable: 'unreadable',
};

/** A file the reader can open, edit and re-upload, rather than having to work
 *  out the format from a sentence. Covers the interesting cases on purpose:
 *  one that translates exactly, one that loses a setting, and one we refuse. */
const EXAMPLE_URLS = `# Your image URLs, one per line. Lines starting with # are ignored.
# Paste these from your page templates, or export them from your CDN logs.

https://res.cloudinary.com/your-cloud-name/image/upload/w_300,h_300,c_fill/products/shoe.jpg
https://res.cloudinary.com/your-cloud-name/image/upload/q_auto,f_auto/banners/hero.jpg
https://res.cloudinary.com/your-cloud-name/image/upload/e_art:zorro/products/bag.jpg
https://res.cloudinary.com/your-cloud-name/video/upload/promo.mp4
https://ik.imagekit.io/your-endpoint-id/products/bag.jpg?tr=w-400,h-400,fo-face
`;

const EXAMPLE_URLS_CSV = `url,page
https://res.cloudinary.com/your-cloud-name/image/upload/w_300,h_300,c_fill/products/shoe.jpg,/products/shoe
https://res.cloudinary.com/your-cloud-name/image/upload/q_auto,f_auto/banners/hero.jpg,/
https://res.cloudinary.com/your-cloud-name/video/upload/promo.mp4,/about
`;

const PLACEHOLDER = `https://res.cloudinary.com/acme/image/upload/w_300,h_300,c_fill/products/shoe.jpg
https://res.cloudinary.com/acme/image/upload/q_auto,f_auto/banners/hero.jpg
https://ik.imagekit.io/acme/products/bag.jpg?tr=w-400,h-400,fo-face`;

/**
 * Pull the URL out of each row of a CSV.
 *
 * Deliberately forgiving rather than a full CSV implementation: a customer's
 * export has whatever columns their CMS emits, and the only one we care about
 * is the one holding a URL or a path. Quoted fields are unwrapped so a value
 * containing a comma survives; a header row is skipped because no cell in it
 * looks like a URL.
 */
function csvToLines(raw: string): string {
  const out: string[] = [];
  for (const line of raw.split(/\r?\n/)) {
    if (!line.trim() || line.trim().startsWith('#')) continue;
    // Split on commas that are not inside quotes.
    const cells = (line.match(/("([^"]|"")*"|[^,]*)(,|$)/g) || [])
      .map((c) => c.replace(/,$/, '').trim().replace(/^"|"$/g, '').replace(/""/g, '"'))
      .filter(Boolean);
    const url = cells.find((c) => /^https?:\/\//i.test(c)) || cells.find((c) => c.includes('/'));
    if (url) out.push(url);
  }
  return out.join('\n');
}

export const CheckUrlsModal: React.FC = () => {
  const { closeModal } = useModal();
  const { showAlert } = useAlerts();
  const scan = useScanMigrationUrlsMutation();

  const [urls, setUrls] = React.useState('');
  const [result, setResult] = React.useState<MigrationScanResult | null>(null);
  // Which verdict the customer is currently looking at. The summary answers
  // "should I worry"; clicking a number answers "which ones".
  const [filter, setFilter] = React.useState<MigrationScanVerdict | null>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);

  const lineCount = urls.split('\n').filter((l) => l.trim() && !l.trim().startsWith('#')).length;

  const run = async () => {
    if (!urls.trim()) {
      showAlert('Paste or upload some URLs first');
      return;
    }
    try {
      setResult(await scan.mutateAsync(urls));
      setFilter(null);
    } catch (err) {
      showAlert(errorText(err, 'Could not check those URLs'));
    }
  };

  const onFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const raw = await file.text();
      // A .csv has to be flattened to one URL per line before it goes to the
      // scanner, which is line-based. Pasting a spreadsheet row straight in
      // would make `shoe.jpg,/products/shoe` look like one very odd URL.
      const text = /\.csv$/i.test(file.name) ? csvToLines(raw) : raw;
      // Added to what is there, so someone collecting URLs from several exports
      // does not lose the first by opening the second.
      setUrls((prev) => (prev.trim() ? `${prev.trim()}\n${text}` : text));
    } catch {
      showAlert('Could not read that file');
    } finally {
      event.target.value = '';
    }
  };

  const dropped = React.useMemo(
    () => Object.entries(result?.dropped || {}).sort((a, b) => b[1] - a[1]),
    [result],
  );
  const refusals = React.useMemo(
    () => Object.entries(result?.refusals || {}).sort((a, b) => b[1] - a[1]),
    [result],
  );
  const pct = (n: number) => (result?.total ? Math.round((100 * n) / result.total) : 0);

  const toggle = (v: MigrationScanVerdict) => setFilter((cur) => (cur === v ? null : v));
  const filtered = React.useMemo(
    () => (filter ? (result?.rows || []).filter((r) => r.verdict === filter) : []),
    [filter, result],
  );

  /** The whole per-URL verdict as a spreadsheet — the shape somebody actually
   *  works through a list of pages to fix in. */
  const downloadReport = () => {
    const rows = result?.rows || [];
    const header = 'url,result,details\n';
    const body = rows
      .map((r) => {
        const details = r.refused
          ? REFUSAL_COPY[r.refused] || r.refused
          : (r.dropped || []).join(' ');
        return `"${r.url.replace(/"/g, '""')}","${VERDICT_CSV[r.verdict]}","${details.replace(/"/g, '""')}"`;
      })
      .join('\n');
    saveAs(new Blob([header + body], { type: 'text/csv;charset=utf-8' }), 'url-check.csv');
  };

  return (
    // BaseModal puts `className` on BOTH the full-screen backdrop and the
    // dialog inside it, so sizing through it shrinks the overlay and the
    // dialog stops being centred. `containerStyle` reaches only the dialog.
    <BaseModal containerStyle={MODAL_SIZE}>
      <ModalHeader
        title="Check your URLs"
        lead="Paste image URLs from your website — a few dozen from your page templates, or the full set
              exported from your logs. We check which URL parameters can be translated, without comparing
              rendered images or checking access. Nothing is created or changed."
      />

      <Textarea value={urls} onChange={setUrls} rows={7} placeholder={PLACEHOLDER} />

      <div className={styles.sectionActions}>
        <Button onClick={run} loading={scan.isLoading} disabled={!urls.trim()}>
          Check {lineCount > 0 ? `${lineCount} URL${lineCount === 1 ? '' : 's'}` : 'these URLs'}
        </Button>
        <Button mode="secondary" outlined onClick={() => fileRef.current?.click()}>
          Upload a .txt or .csv
        </Button>
        <ExampleFileLink filename="example-urls.txt" contents={EXAMPLE_URLS} label="Example .txt" />
        <ExampleFileLink filename="example-urls.csv" contents={EXAMPLE_URLS_CSV} label="Example .csv" />
        {urls.trim() && (
          <Button
            mode="flat"
            onClick={() => {
              setUrls('');
              setResult(null);
            }}
          >
            Clear
          </Button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept=".txt,.csv,text/plain,text/csv"
          className={styles.hiddenFile}
          onChange={onFile}
        />
      </div>

      {result && (
        <div className={styles.report}>
          <SegmentBar
            segments={[
              { value: result.clean, tone: 'good', label: 'translated' },
              { value: result.approximate, tone: 'warn', label: 'settings changed' },
              { value: result.refused, tone: 'bad', label: 'will not work' },
              { value: result.unparseable, tone: 'neutral', label: 'unreadable' },
            ]}
          />

          {/* Each number is a filter. "17% will not work" is only useful if the
              next click shows WHICH ones, and that was the piece missing. */}
          <div className={styles.statRow}>
            <Stat
              tone="good"
              value={`${pct(result.clean)}%`}
              label={`Parameters translated (${result.clean})`}
              hint="The URL parameters can be translated. Click to list them."
              onClick={result.clean ? () => toggle('identical') : undefined}
              selected={filter === 'identical'}
            />
            <Stat
              tone="warn"
              value={`${pct(result.approximate)}%`}
              label={`Slightly different (${result.approximate})`}
              hint="The image still works; a setting could not be carried over. Click to list them."
              onClick={result.approximate ? () => toggle('approximate') : undefined}
              selected={filter === 'approximate'}
            />
            <Stat
              tone="bad"
              value={`${pct(result.refused)}%`}
              label={`Will not work (${result.refused})`}
              hint="Something we deliberately do not serve, such as video. Click to list them."
              onClick={result.refused ? () => toggle('refused') : undefined}
              selected={filter === 'refused'}
            />
            <Stat
              value={result.unparseable}
              label="Unreadable"
              hint="Lines that were not a URL. Click to list them."
              onClick={result.unparseable ? () => toggle('unparseable') : undefined}
              selected={filter === 'unparseable'}
            />
          </div>

          {filtered.length > 0 && (
            <>
              <h4 className={styles.reportTitle}>
                {VERDICT_TITLE[filter as MigrationScanVerdict]} ({filtered.length})
              </h4>
              <ul className={styles.exampleList}>
                {filtered.slice(0, 50).map((row) => (
                  <li key={row.url}>
                    {(row.dropped?.length || row.refused) && (
                      <span
                        className={cn(
                          styles.exampleTag,
                          row.refused ? styles.exampleTag_bad : styles.exampleTag_warn,
                        )}
                        title={row.refused ? REFUSAL_COPY[row.refused] || row.refused : undefined}
                      >
                        {row.refused
                          ? REFUSAL_SHORT[row.refused] || row.refused
                          : row.dropped?.join(' ')}
                      </span>
                    )}
                    <code className={styles.exampleUrl}>{row.url}</code>
                  </li>
                ))}
              </ul>
              {filtered.length > 50 && (
                <p className={styles.rowHelp}>
                  Showing the first 50 of {filtered.length}. Download the report for all of them.
                </p>
              )}
            </>
          )}

          <div className={styles.sectionActions}>
            <Button mode="secondary" outlined onClick={downloadReport} disabled={!result.rows?.length}>
              Download the full report (CSV)
            </Button>
            {result.rows_truncated && (
              <span className={styles.rowHelp}>
                Your list was very long, so the report covers the first 5,000 URLs.
              </span>
            )}
          </div>

          {/* The most useful line here for a Cloudinary customer. ImageKit's own
              Cloudinary importer answers 404 on a signature segment, so a
              signed-URL customer cannot migrate to them at all. We strip it and
              serve — a green tick where the alternative is a hard stop. */}
          {result.signed_urls > 0 && (
            <Note tone="good">
              <strong>{result.signed_urls} of your URLs are signed, and all of them work.</strong> Worth
              knowing: ImageKit&rsquo;s own Cloudinary importer answers <code>404</code> on a signed URL,
              so those cannot be moved to them at all.
            </Note>
          )}

          {dropped.length > 0 && (
            <>
              <h4 className={styles.reportTitle}>Settings we cannot carry over</h4>
              <p className={styles.rowHelp}>
                The image still works — these are the instructions that will not be applied. Most common
                first, so you know which are worth a look.
              </p>
              <table className={styles.table}>
                <tbody>
                  {dropped.map(([token, count]) => (
                    <tr key={token}>
                      <td>
                        <code>{token}</code>
                      </td>
                      <td className={styles.tableNum}>
                        {count} URL{count === 1 ? '' : 's'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {refusals.length > 0 && (
            <>
              <h4 className={styles.reportTitle}>URLs that will not work</h4>
              <table className={styles.table}>
                <tbody>
                  {refusals.map(([reason, count]) => (
                    <tr key={reason}>
                      <td>{REFUSAL_COPY[reason] || reason}</td>
                      <td className={styles.tableNum}>{count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {result.examples && result.examples.length > 0 && (
            <>
              <h4 className={styles.reportTitle}>Examples from your list</h4>
              <ul className={styles.exampleList}>
                {result.examples.map((ex) => (
                  <li key={ex.url}>
                    <span
                      className={cn(
                        styles.exampleTag,
                        ex.refused ? styles.exampleTag_bad : styles.exampleTag_warn,
                      )}
                      title={ex.refused ? REFUSAL_COPY[ex.refused] || ex.refused : undefined}
                    >
                      {ex.refused ? REFUSAL_SHORT[ex.refused] || ex.refused : ex.dropped?.join(' ')}
                    </span>
                    <code className={styles.exampleUrl}>{ex.url}</code>
                  </li>
                ))}
              </ul>
            </>
          )}

          {result.clean === result.total && result.total > 0 && (
            <Note tone="good">
              Every URL in this list has translatable parameters. Rendering can differ between providers;
              this check does not verify that an image exists or can be accessed.
            </Note>
          )}
        </div>
      )}

      <div className={styles.modalActions}>
        <Button mode="secondary" outlined onClick={closeModal}>
          Done
        </Button>
      </div>
    </BaseModal>
  );
};

export default CheckUrlsModal;
